import { CofConfigId, type ConfigResult } from "../../gql-generated/types";
import { getKeys } from "../../shared/utils/object";

export type ConfigDefault<TType = never, TValue = never> = Partial<Omit<ConfigResult, "__typename" | "value">> & {
    type: TType;
    localDefault: TValue;
};

type ConfigDefaultTypes = ConfigDefault<"boolean", boolean> | ConfigDefault<"string", string>;

export const cofConfigIdsMap: Record<CofConfigId, ConfigDefaultTypes> = {
    [CofConfigId.MyLensesWebLensPublication]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesCreatorPayout]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesCreatorPayoutNoApplication]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesDiscoverabilityTagsV1]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesFeedbackRating]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesMaintenanceMode]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesUiWebApis]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesCta]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesBanner]: { type: "string", localDefault: "{}" },
    [CofConfigId.MyLensesUxImprovements]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesQara]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesPortalUnification]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesUiAssetLibrary]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesHostedLenses]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesRemoteAssets]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesLensCreatorRewardsInsights]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesGlobalNav]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesTrendingTags]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesLensCreatorRewardsDetails]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesRemoteApiHttp]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesUiCommunityAudience]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesUiCommunityAudienceMetricsHidden]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesRemoteApiBuiltInProcessor]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesSpectaclesSubmission]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesLensDescription]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesCreatorRewardRecord]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesOrgsV2]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesLensChallengeTags]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesLensCloudProvisioning]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesGraphqlFederation]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesLensMakerDebug]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesSpectaclesRnTagsMultiPreviews]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesSpectaclesVideoPreview]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesGameToggle]: { type: "boolean", localDefault: true },
    // TODO: cleanup - 100% rolled out, add above
    [CofConfigId.MyLensesDeprecateBusinessFlow]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesDisableInDiscover]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesIldgFtue]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesIldgFtueBanner]: { type: "string", localDefault: "{}" },
    [CofConfigId.MyLensesIlp]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesLensTaxonomy]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesManageMembersButton]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesOrgLevelScope]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesStandardEventReporting]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesSubmissionErrorReporting]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesUiFgTest]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesSpecsCommerce]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesHomeTab]: { type: "boolean", localDefault: true },
    [CofConfigId.MyLensesMonetizationInsights]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesSubmissionQuotas]: { type: "boolean", localDefault: false },
    [CofConfigId.MyLensesLensPlusPhoneVerification]: { type: "boolean", localDefault: false },
    // TODO cleanup - add below only after 100% rolled out
    [CofConfigId.MyLensesChat]: { type: "boolean", localDefault: false },
} as const;

// TODO: remove deprecated feature gates
const deprecated = new Set<CofConfigId>([
    CofConfigId.MyLensesGameToggle,
    CofConfigId.MyLensesDeprecateBusinessFlow,
    CofConfigId.MyLensesDisableInDiscover,
    CofConfigId.MyLensesIldgFtue,
    CofConfigId.MyLensesIldgFtueBanner,
    CofConfigId.MyLensesIlp,
    CofConfigId.MyLensesLensTaxonomy,
    CofConfigId.MyLensesManageMembersButton,
    CofConfigId.MyLensesOrgLevelScope,
    CofConfigId.MyLensesStandardEventReporting,
    CofConfigId.MyLensesSubmissionErrorReporting,
    CofConfigId.MyLensesUiFgTest,
]);

export const cofConfigIds = getKeys(cofConfigIdsMap).filter((configId) => !deprecated.has(configId));
